import type {ViewSharingSettingsData} from "../../../generated/api/base";
import {Permission, XyiconFeature} from "../../../generated/api/base";
import type {User} from "../../../data/models/User";
import type {UserGroup} from "../../../data/models/UserGroup";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {ShareOptionV5} from "../details/ShareOptionV5";
import {ReactUtils} from "../../utils/ReactUtils";
import {ButtonV5} from "../button/ButtonV5";
import type {ReportSharingSettingsData} from "../../../generated/api/reports";
import type {Portfolio} from "../../../data/models/Portfolio";
import {Flex1} from "../styles/styles";
import {RowStyled} from "./SharingPanel.styled";
import type {IGlobalViewFolderSharingSettingsData} from "./SharingPopupV5";
import type {SelectedItem} from "./SharingUtils";
import {ItemWithInitials} from "./ItemWithInitials";

interface IEditViewSharingProps {
	readonly items: (User | UserGroup | Portfolio)[];
	readonly addedItems: (User | UserGroup | Portfolio)[];
	readonly feature: XyiconFeature.User | XyiconFeature.UserGroup | XyiconFeature.Portfolio;
	readonly selectedItemList?: SelectedItem[];
	readonly sharingList?: (ViewSharingSettingsData | ReportSharingSettingsData | IGlobalViewFolderSharingSettingsData)[];
	readonly isSharing?: boolean;
	readonly showShareOption?: boolean;
	readonly ownedByUserId?: string;
	readonly isGlobal?: boolean;
	readonly isLocked?: boolean;
	readonly itemLabel?: "View" | "Report" | "View Folder" | "Global View" | "Global Report";
	readonly onDeleteItem: (item: any) => void;
	readonly onItemSelect?: (item: any) => void;
	readonly onItemRemove?: (items: any) => void;
	readonly setUserPermission?: (permission: number, sharingItem: ViewSharingSettingsData | ReportSharingSettingsData) => void;
	readonly setUserGroupPermission?: (item: any, sharingList: any) => void;
	readonly hasEdit?: (userOrUserGroup?: User | UserGroup) => boolean;
}

export const GroupShareSelectorV5 = (props: IEditViewSharingProps) => {
	const {
		items,
		addedItems,
		selectedItemList,
		sharingList,
		isSharing,
		showShareOption,
		ownedByUserId,
		isGlobal,
		isLocked,
		itemLabel,
		feature,
		onDeleteItem,
		onItemSelect,
		onItemRemove,
		setUserPermission,
		setUserGroupPermission,
		hasEdit,
	} = props;

	const isAdded = (item: User | UserGroup | Portfolio) => addedItems.some((addedItem) => addedItem.id === item.id);

	const toggleSelect = (item: User | UserGroup | Portfolio) => {
		if (!isAdded(item)) {
			if (!selectedItemList.some((selectedItem) => selectedItem.id === item.id)) {
				onItemSelect(item);
			} else {
				onItemRemove(item);
			}
		}
	};

	return (
		<>
			{items
				.toSorted((a, b) => {
					if (feature === XyiconFeature.User) {
						return StringUtils.sortIgnoreCase((a as User).fullName, (b as User).fullName);
					} else if (feature === XyiconFeature.UserGroup) {
						return StringUtils.sortIgnoreCase((a as UserGroup).name, (b as UserGroup).name);
					} else if (feature === XyiconFeature.Portfolio) {
						return StringUtils.sortIgnoreCase((a as Portfolio).name, (b as Portfolio).name);
					}
				})
				.map((item) => {
					let sharingItem: ViewSharingSettingsData | ReportSharingSettingsData | undefined;
					let hasUpdatePermission = false;
					const isSelected = selectedItemList?.some((selectedItem) => selectedItem.id === item.id);
					const isAdministratorUserGroup = feature === XyiconFeature.UserGroup && (item as UserGroup).name === "Administrators";

					if (sharingList) {
						sharingItem = sharingList?.find((sharing) =>
							feature === XyiconFeature.User ? sharing.userID === item.id : sharing.userGroupID === item.id,
						);
						hasUpdatePermission =
							(sharingItem as ViewSharingSettingsData)?.canEditSharedView || (sharingItem as ReportSharingSettingsData)?.canEditSharedReport || false;
					}
					const displayedUpdatePermission = itemLabel === "Global Report" ? isAdministratorUserGroup : hasUpdatePermission;

					return (
						<RowStyled
							key={item.id}
							className={ReactUtils.cls({
								unregistered: feature === XyiconFeature.User && (item as User).status === "invited",
								selected: isSelected,
							})}
							$isSelectable={!isAdded(item)}
							onClick={() => toggleSelect(item)}
						>
							<ItemWithInitials
								item={item}
								variant="big"
							/>
							<Flex1 />
							{!isLocked && item.id !== ownedByUserId && isAdded(item) && (!isGlobal || (isGlobal && !isAdministratorUserGroup)) && (
								<ButtonV5
									label={isSharing ? "Unshare" : "Remove"}
									title={isSharing ? "Unshare" : "Remove"}
									className="unshare"
									onClick={(e) => onDeleteItem(item)}
								/>
							)}
							{sharingList &&
								showShareOption &&
								((isGlobal && isAdministratorUserGroup) || itemLabel === "Global Report" ? (
									<span className="sharedStyles disabled">{displayedUpdatePermission ? "Can Edit" : "Can View"}</span>
								) : (
									<ShareOptionV5
										value={hasUpdatePermission ? Permission.Update : Permission.View}
										onChange={
											feature === XyiconFeature.User
												? (permission) => setUserPermission(permission, sharingItem)
												: (permission) => setUserGroupPermission(permission, sharingItem)
										}
										hasOwner={false}
										hasEdit={hasEdit?.(item as User | UserGroup)}
									/>
								))}
						</RowStyled>
					);
				})}
		</>
	);
};
